import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Modal, Popconfirm, Row, Space, Table, Tag } from 'antd';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { RiCloseFill, RiEditLine, RiErrorWarningLine } from 'react-icons/ri';
import { MdFileDownloadDone } from 'react-icons/md';
import { useIntl } from 'react-intl';
import { PiDownload, PiUpload } from 'react-icons/pi';
import isEqual from 'lodash/isEqual';
import useNavigationSource from '../../../../../app/hooks/useNavigationSource';
import { useGetLanguagesQuery } from '../../../api/languagesApiSlice';
import appRoutes from '@/configs/routes';
import { DICTIONARY_STATUS } from '../../Dictionaries/PageDictionaries/PageDictionaries.const';
import { PrivateComponent } from '../../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../../const/permissions';
import makeTableLoadingObject from '../../../../../lib/makeTableLoadingObject';
import TranslationCell from '../../../components/TranslationCell';
import useToggle from '../../../../../app/hooks/useToggle';
import { ActionButton, BreadCrumbs, IntlMessages } from '@/components';
import useDownload from '../../../../../app/hooks/useDownload';
import handleResponse from '../../../../../lib/handleResponse';
import FormImportDictionaries from '../../../forms/FormImportDictionaries';
import getFeatureSourceTranslation from '../../../../Featured/utils/getFeatureSourceTranslation';
import {
  getDataSource,
  getTranslationListTableColumns,
  handleSelectPrevTranslation,
  handleUpdateTranslationValue,
} from './PageTranslationsDetails.const';
import {
  useGetDictionaryQuery,
  useImportDictionaryMutation,
  useSaveDictionaryTranslationsMutation,
  useUpdateDictionaryMutation,
} from '../../../api/dictionariesApiSlice';
import styles from './PageTranslationsDetails.module.css';

const PageTranslationsDetails = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const [searchQueryParams] = useSearchParams();
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { urlPrefix, navigationSource } = useNavigationSource();

  const [initialTranslationData, setInitialTranslationData] = useState([]);
  const [translationData, setTranslationData] = useState([]);
  const [isSelecting, toggleSelecting] = useToggle();
  const [isActiveImportModal, toggleImportModal] = useToggle();
  const timestampRef = useRef(Date.now()).current; // ugly hack for force rerender
  const [item, setItem] = useState(0); // ugly hack for force rerender

  const multipleSources = searchQueryParams.get('multipleSources') ?? null;

  const { data: { data: languageData = [] } = { data: [], pagination: {} }, isFetching: isLanguagesLoading } =
    useGetLanguagesQuery({ queryParams: 'search=status:enabled&limit=0' });

  const [saveDictionaryTranslations, { isLoading: isTranslationsLoading }] =
    useSaveDictionaryTranslationsMutation();
  const [updateDictionary, { isLoading: isUpdatingDictionary }] = useUpdateDictionaryMutation();
  const [importDictionary, { isLoading: isImportingDictionary }] = useImportDictionaryMutation();

  const { data: dictionaryDetails = { translations: { data: [] } }, isFetching } = useGetDictionaryQuery({
    id,
    include: 'translations,structure,structure.fields.translations',
    sessionId: timestampRef + item,
  });

  const structure = isFetching ? {} : dictionaryDetails.structure?.data;
  const translationsData = dictionaryDetails?.translations?.data ?? [];
  const isReady = dictionaryDetails.id ? dictionaryDetails.status === DICTIONARY_STATUS.READY : true;
  const hasChanges = !isEqual(initialTranslationData, translationData);
  const availableLanguages =
    isLanguagesLoading || isFetching
      ? []
      : languageData.filter((language) => structure.language_ids.indexOf(language.id) !== -1);

  useEffect(() => {
    if (translationsData) {
      setInitialTranslationData(translationsData);
      setTranslationData(translationsData);
    }
  }, [isFetching]);

  const handleImportDictionary = (values) => {
    const formData = new FormData();

    formData.append('file', values.file);

    importDictionary({ id, formData })
      .unwrap()
      .then(() =>
        handleResponse('success', intl, 'dictionaries-import-success', [
          () => setItem((prevState) => prevState + 1),
        ]),
      )
      .catch((error) => handleResponse('error', intl, 'dictionaries-import-failed', [], error))
      .finally(() => toggleImportModal());
  };

  const findTranslation = (field) =>
    translationData.find(
      (tr) => tr.structure_field_id === field.field_id && tr.language_id === field.language_id,
    );

  const handleChange = (val, field) => handleUpdateTranslationValue(val, field, setTranslationData);

  const handleSelect = (dictionary_id, field) => {
    toggleSelecting();
    handleSelectPrevTranslation(dictionary_id, field, dispatch, setTranslationData, toggleSelecting);
  };

  const handleSave = () => {
    saveDictionaryTranslations({ id, values: { data: translationData } })
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-updated-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-update-failed', [], error));
  };

  const handleMakeReady = () => {
    updateDictionary({ dictionaryId: id, values: { status: DICTIONARY_STATUS.READY } })
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-updated-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-update-failed', [], error));
  };

  const handleRenderTranslationCell = (language, field) => {
    const translation = findTranslation({ field_id: field.id, language_id: language.id });

    return (
      <TranslationCell
        field={{
          id: null,
          text: translation?.text ?? '',
          language_id: language.id,
          field_id: field.id,
          label: field.field,
          dictionary_id: dictionaryDetails.id,
        }}
        availableLanguages={availableLanguages}
        dictionaryDetails={dictionaryDetails}
        language={language}
        min={field.validation.min}
        max={field.validation.max || 1000}
        handleChange={handleChange}
        handleSelect={handleSelect}
      />
    );
  };

  const { download } = useDownload();

  const onDownload = async (dictionary_id) => {
    download(`dictionaries/${dictionary_id}/exports`);
  };

  const dictionaryName = () => {
    if (!dictionaryDetails.id) {
      return null;
    }

    return (
      <div className="hp-d-flex-center">
        {`${dictionaryDetails.name} (v${dictionaryDetails?.structure?.data?.version})`}
        &nbsp;
        <Tag color={isReady ? 'success' : ''}>
          <IntlMessages id={isReady ? 'dictionaries-status-ready' : 'dictionaries-status-draft'} />
        </Tag>
      </div>
    );
  };

  return (
    <>
      <Row gutter={[32, 32]} justify="space-between" className="hp-mb-12" align="middle">
        <BreadCrumbs
          breadCrumbParent={
            urlPrefix ? (
              <Link to={appRoutes.urlPageFeatures({ featureSource: urlPrefix })}>
                <IntlMessages id={getFeatureSourceTranslation(urlPrefix)} />
              </Link>
            ) : null
          }
          breadCrumbParent2={
            <Link to={appRoutes.urlPageDictionaries({ urlPrefix, source: navigationSource, multipleSources })}>
              <IntlMessages id="dictionaries-breadcrumbs" />
            </Link>
          }
          breadCrumbActive={<IntlMessages id="translations-breadcrumbs" />}
        />

        <Col>
          <Space>
            <PrivateComponent allowedPermissions={[Permissions.TRANSLATIONS.DICTIONARIES.FILES.EXPORT]}>
              <ActionButton
                title={<IntlMessages id="ui-general-export" />}
                icon={<PiDownload className="btn-icon-mr-1" data-testid="export-icon" />}
                onClick={() => onDownload(id)}
              />
            </PrivateComponent>
            <PrivateComponent allowedPermissions={[Permissions.TRANSLATIONS.DICTIONARIES.FILES.IMPORT]}>
              <ActionButton
                title={<IntlMessages id="ui-general-import" />}
                icon={<PiUpload className="btn-icon-mr-1" />}
                onClick={toggleImportModal}
              />
            </PrivateComponent>
            <PrivateComponent allowedPermissions={[Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.VIEW]}>
              <ActionButton
                title={<IntlMessages id="translations-edit-button" />}
                icon={<RiEditLine className="btn-icon-mr-1" />}
                onClick={() =>
                  navigate(
                    appRoutes.urlPageDictionaryEdit({ urlPrefix, id, source: navigationSource, multipleSources }),
                  )
                }
              />
            </PrivateComponent>
            {!isReady && (
              <PrivateComponent allowedPermissions={[Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.EDIT]}>
                <Popconfirm
                  title={
                    <div>
                      <IntlMessages id="dictionaries-update-status-confirm-message" />
                      <br />
                      <span className="hp-input-description">
                        <IntlMessages id="dictionaries-update-status-confirm-message-danger" />
                      </span>
                    </div>
                  }
                  onConfirm={handleMakeReady}
                  placement="bottomRight"
                  okText={<IntlMessages id="ui-general-yes" />}
                  cancelText={<IntlMessages id="ui-general-no" />}
                  icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
                >
                  <Button
                    type="primary"
                    ghost
                    disabled={isFetching}
                    icon={<MdFileDownloadDone className="btn-icon-mr-1" />}
                  >
                    <IntlMessages id="ui-dictionaries-status-ready" />
                  </Button>
                </Popconfirm>
              </PrivateComponent>
            )}
          </Space>
        </Col>
      </Row>

      <Alert
        className="hp-mb-24"
        title={<IntlMessages id="translations-alert-notification" />}
        type="info"
        showIcon
      />

      <Modal
        title={<IntlMessages id="translations-import-title" />}
        width={520}
        centered
        destroyOnHidden
        open={isActiveImportModal}
        onCancel={toggleImportModal}
        footer={null}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <FormImportDictionaries
          warningText={<IntlMessages id="translations-import-warning" />}
          onSubmit={handleImportDictionary}
          isSubmitting={isImportingDictionary}
        />
      </Modal>

      <Card title={dictionaryName()} className="hp-border-color-black-40 hp-card-6">
        <Table
          size="small"
          bordered
          loading={makeTableLoadingObject(
            isLanguagesLoading || isFetching || isTranslationsLoading || isUpdatingDictionary || isSelecting,
          )}
          rowKey="field"
          className={styles.topContentTable}
          columns={getTranslationListTableColumns(availableLanguages, handleRenderTranslationCell)}
          dataSource={
            !isFetching && !isLanguagesLoading
              ? getDataSource(dictionaryDetails, structure, languageData)
              : []
          }
          pagination={false}
          scroll={{ x: 320 + availableLanguages.length * 220 }}
        />
      </Card>

      <Row gutter={[16, 16]} justify="end" className="hp-mt-24">
        <Col>
          <Button
            onClick={() => {
              navigate(state?.key === 'prev_dictionary_edit' ? -2 : -1);
            }}
          >
            <IntlMessages id="ui-general-back" />
          </Button>
        </Col>
        <Col>
          <Button type="primary" disabled={!hasChanges || isFetching} onClick={handleSave}>
            <IntlMessages id="ui-general-save" />
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default PageTranslationsDetails;
